import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import {
  CaretDownIcon,
  CheckIcon,
  PlusIcon,
  SlidersHorizontalIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  CATALOG_BASE_FILTER_COLUMNS,
  CATALOG_VALUELESS_OPERATORS,
} from '@erp/rpc';
import type {
  CatalogAdvancedFilter,
  CatalogFilterMatch,
  CatalogFilterOperator,
} from '@erp/rpc';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { NO_ADVANCED_FILTERS, sameAdvancedFilters } from './catalogFilters';
import {
  columnComboboxState,
  type FilterColumnOption,
} from './columnComboboxState';

export type { FilterColumnOption };

// One selectable filter column — see `FilterColumnOption` in
// `columnComboboxState.ts`. `label` is what the user picks by; `value` is the
// name the filter is sent as. They differ on the review page's export
// worksheets, whose headings ("PRODUCT NUMBER") are not their AS/400 field
// codes ("ITEMNO").

// One draft constraint row. `id` is a stable client-only key so React can track
// rows across add/remove without reordering glitches. A row only becomes an
// applied filter once it has a column (and a value, unless the operator is
// value-less) — see `isRowApplied`.
interface DraftRow {
  id: string;
  column: string;
  operator: CatalogFilterOperator;
  value: string;
}

interface OperatorOption {
  value: CatalogFilterOperator;
  label: string;
}

// Human labels for the base ProductV3 columns, keyed by the contract column name.
const BASE_COLUMN_LABELS: Record<
  (typeof CATALOG_BASE_FILTER_COLUMNS)[number],
  string
> = {
  displaySku: 'SKU',
  displayDescription: 'Description',
  upc: 'UPC',
  isSellable: 'Sellable',
  isActive: 'Active',
};

// Draft rows for an already-applied filter set — used to rehydrate the panel
// from a restored session (see `initialFilters`). Every seeded row is applied by
// construction, so it round-trips back to the same filter set.
function toDraftRows(filters: CatalogAdvancedFilter[]): DraftRow[] {
  return filters.map((filter, index) => ({
    id: `f${index + 1}`,
    column: filter.column,
    operator: filter.operator,
    value: filter.value,
  }));
}

function isRowApplied(row: DraftRow): boolean {
  if (!row.column.trim()) return false;
  if (CATALOG_VALUELESS_OPERATORS.has(row.operator)) return true;
  return row.value.trim().length > 0;
}

function toFilter(row: DraftRow): CatalogAdvancedFilter {
  // Drop the value for value-less operators so a stale draft value isn't sent
  // (the raw value stays in row state for when the user switches back).
  const valueless = CATALOG_VALUELESS_OPERATORS.has(row.operator);
  return {
    column: row.column.trim(),
    operator: row.operator,
    value: valueless ? '' : row.value,
  };
}

/** The filter set a list of draft rows projects to — the applied rows, in order. */
function appliedFilters(rows: DraftRow[]): CatalogAdvancedFilter[] {
  const applied: CatalogAdvancedFilter[] = [];
  for (const row of rows) {
    if (isRowApplied(row)) applied.push(toFilter(row));
  }
  return applied;
}

// ─── Column combobox ──────────────────────────────────────────────────────────

// One selectable column in the picker: the shared row + selected check, with the
// caller supplying the label layout its group wants (a base column shows its
// human label beside the raw name; a metadata column its description).
function ColumnOption({
  column,
  selected,
  onSelect,
  children,
}: {
  column: string;
  selected: boolean;
  onSelect: (column: string) => void;
  children: ReactNode;
}) {
  return (
    <CommandItem value={column} onSelect={() => onSelect(column)}>
      <CheckIcon
        className={cn('mr-2 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')}
      />
      {children}
    </CommandItem>
  );
}

function ColumnCombobox({
  value,
  columns,
  includeBaseColumns,
  columnsLoading,
  onChange,
}: {
  value: string;
  columns: FilterColumnOption[];
  includeBaseColumns: boolean;
  columnsLoading: boolean;
  onChange: (column: string) => void;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Ties the combobox trigger to its popup listbox for assistive tech (and
  // satisfies the `combobox` role's required `aria-controls`).
  const listboxId = useId();

  const baseColumns = includeBaseColumns
    ? CATALOG_BASE_FILTER_COLUMNS.map((c) => ({
        name: c,
        label: BASE_COLUMN_LABELS[c],
      }))
    : [];
  const {
    matchedBase,
    matchedColumns,
    showCustom,
    showEmpty,
    showLoading,
    trimmed,
    selectedLabel,
  } = columnComboboxState({
    search,
    value,
    columns,
    columnsLoading,
    baseColumns,
  });

  const select = (column: string) => {
    onChange(column);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          className="h-8 w-56 justify-between text-xs font-normal"
        >
          <span
            className={cn(
              'truncate',
              !selectedLabel && 'text-muted-foreground',
            )}
          >
            {selectedLabel ??
              gt('Select column', {
                context: 'placeholder for advanced filter column picker',
              })}
          </span>
          <CaretDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={gt('Search columns...', {
              context: 'placeholder for advanced filter column search',
            })}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList id={listboxId}>
            {showEmpty && (
              <CommandEmpty>
                <T context="empty state when no columns match the search">
                  No columns found
                </T>
              </CommandEmpty>
            )}
            {showCustom && (
              <CommandGroup
                heading={gt('Custom', {
                  context: 'group heading for a custom typed filter column',
                })}
              >
                <CommandItem
                  value={`__custom__${trimmed}`}
                  onSelect={() => select(trimmed)}
                >
                  <PlusIcon className="mr-2 h-4 w-4" />
                  {gt('Use "{column}"', {
                    context: 'menu item to use a custom typed column name',
                    column: trimmed,
                  })}
                </CommandItem>
              </CommandGroup>
            )}
            {matchedBase.length > 0 && (
              <CommandGroup
                heading={gt('Product', {
                  context: 'group heading for base product columns',
                })}
              >
                {matchedBase.map((c) => (
                  <ColumnOption
                    key={c.name}
                    column={c.name}
                    selected={value === c.name}
                    onSelect={select}
                  >
                    <span className="flex-1 truncate">{c.label}</span>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {c.name}
                    </span>
                  </ColumnOption>
                ))}
              </CommandGroup>
            )}
            {showLoading && (
              <CommandGroup
                heading={gt('Columns', {
                  context: 'group heading for the filterable view columns',
                })}
              >
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  {gt('Loading columns...', {
                    context:
                      'placeholder in the advanced filter column picker while ingest columns load',
                  })}
                </div>
              </CommandGroup>
            )}
            {matchedColumns.length > 0 && (
              <CommandGroup
                heading={gt('Columns', {
                  context: 'group heading for the filterable view columns',
                })}
              >
                {matchedColumns.map((c) => (
                  <ColumnOption
                    key={c.value}
                    column={c.value}
                    selected={value === c.value}
                    onSelect={select}
                  >
                    <span className="flex-1 truncate font-mono text-xs">
                      {c.label}
                    </span>
                    {c.hint && (
                      <span className="ml-2 truncate text-xs text-muted-foreground">
                        {c.hint}
                      </span>
                    )}
                  </ColumnOption>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Filter row ───────────────────────────────────────────────────────────────

/**
 * Every filter operator, labelled as this editor names it. Exported so read-only
 * renderings of an applied filter set — the validation rule's row filters, say —
 * read the operator exactly as the author picked it.
 */
export function useCatalogFilterOperatorLabels(): Record<
  CatalogFilterOperator,
  string
> {
  const gt = useGT();
  return {
    contains: gt('Contains', { context: 'filter operator' }),
    not_contains: gt('Does not contain', { context: 'filter operator' }),
    equals: gt('Equals', { context: 'filter operator' }),
    not_equals: gt('Not equals', { context: 'filter operator' }),
    starts_with: gt('Starts with', { context: 'filter operator' }),
    ends_with: gt('Ends with', { context: 'filter operator' }),
    greater_than: gt('Greater than', { context: 'filter operator' }),
    less_than: gt('Less than', { context: 'filter operator' }),
    like: gt('Like (pattern)', { context: 'filter operator' }),
    is_empty: gt('Is empty', { context: 'filter operator' }),
    is_not_empty: gt('Is not empty', { context: 'filter operator' }),
  };
}

// The picker's own ordering, which the label record does not carry.
const OPERATOR_ORDER: CatalogFilterOperator[] = [
  'contains',
  'not_contains',
  'equals',
  'not_equals',
  'starts_with',
  'ends_with',
  'greater_than',
  'less_than',
  'like',
  'is_empty',
  'is_not_empty',
];

function buildOperatorOptions(
  labels: Record<CatalogFilterOperator, string>,
): OperatorOption[] {
  return OPERATOR_ORDER.map((value) => ({ value, label: labels[value] }));
}

// One constraint row: column picker, operator, value, remove. The value input is
// disabled (and its stored value ignored) for the value-less operators.
function FilterRow({
  row,
  columns,
  includeBaseColumns,
  columnsLoading,
  onChange,
  onRemove,
}: {
  row: DraftRow;
  columns: FilterColumnOption[];
  includeBaseColumns: boolean;
  columnsLoading: boolean;
  onChange: (patch: Partial<DraftRow>) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  const operatorOptions = buildOperatorOptions(
    useCatalogFilterOperatorLabels(),
  );
  const valueless = CATALOG_VALUELESS_OPERATORS.has(row.operator);
  return (
    <div className="flex items-center gap-2">
      <ColumnCombobox
        value={row.column}
        columns={columns}
        includeBaseColumns={includeBaseColumns}
        columnsLoading={columnsLoading}
        onChange={(column) => onChange({ column })}
      />
      <Select
        value={row.operator}
        onValueChange={(operator) =>
          onChange({ operator: operator as CatalogFilterOperator })
        }
      >
        <SelectTrigger className="h-8 w-40 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operatorOptions.map((op) => (
            <SelectItem key={op.value} value={op.value} className="text-xs">
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={valueless ? '' : row.value}
        disabled={valueless}
        onChange={(e) => onChange({ value: e.target.value })}
        placeholder={
          valueless
            ? ''
            : gt('Value', {
                context: 'placeholder for advanced filter value input',
              })
        }
        className="h-8 flex-1 text-xs"
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        aria-label={gt('Remove filter', {
          context: 'aria label for advanced filter remove button',
        })}
      >
        <XIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

// The connector drawn between constraint rows. Constraints are ANDed — a row
// has to satisfy all of them — which the panel says outright rather than
// leaving it to be inferred from the rows sitting under one another.
function FilterConjunction() {
  return (
    <div className="flex items-center gap-2 pl-1">
      <span className="rounded border bg-background px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-muted-foreground">
        <T context="connector label between advanced filter constraints, meaning all of them must match">
          AND
        </T>
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

// ─── Advanced filters ───────────────────────────────────────────────────────

export function AdvancedFilters({
  columns,
  columnsLoading = false,
  onApplyChange,
  errorMessage,
  includeBaseColumns = true,
  initialFilters,
  match,
  onMatchChange,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: {
  // The columns this view can be filtered by, in the order they are offered.
  columns: FilterColumnOption[];
  // True while ingest-column suggestions are still loading. Product base
  // columns stay available immediately; the Columns group shows a placeholder
  // so an empty list is not mistaken for "no columns".
  columnsLoading?: boolean;
  // Fires only when the applied set actually changes (not on every keystroke),
  // so incomplete rows don't churn the query or reset pagination.
  onApplyChange: (filters: CatalogAdvancedFilter[]) => void;
  errorMessage?: string | null;
  // Whether to offer the base ProductV3 columns ("Product" group). The Export
  // tab sets this false so only the selected table's columns are offered.
  includeBaseColumns?: boolean;
  // Filters to seed the panel's draft rows with — a restored session's applied
  // set. Read on mount only (this component owns the live draft state); to
  // re-seed it, remount it with a new `key`, which is how the catalog's Reset
  // clears the rows.
  initialFilters?: CatalogAdvancedFilter[];
  // Whether the constraints are met by one source record or by the item across
  // its records, and the setter for the switch that changes it. Unlike the
  // rows, this is fully controlled — it is a discrete choice with nothing to
  // debounce, so the page owns it and the panel only renders it. Omit both to
  // leave the switch out (views that only match record-by-record).
  match?: CatalogFilterMatch;
  onMatchChange?: (match: CatalogFilterMatch) => void;
  // Controlled open state — pass with `hideTrigger` to drive the panel from an
  // external `AdvancedFiltersToggle` (e.g. beside the search bar). Uncontrolled
  // with its own trigger when omitted.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const draft = useDraftFilterRows(initialFilters, onApplyChange);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <InlineFiltersTrigger open={open} activeCount={draft.appliedCount} />
      )}

      <CollapsibleContent className="pt-2">
        <FilterRowsPanel
          rows={draft.rows}
          columns={columns}
          includeBaseColumns={includeBaseColumns}
          columnsLoading={columnsLoading}
          errorMessage={errorMessage}
          match={match}
          onMatchChange={onMatchChange}
          onChangeRow={draft.updateRow}
          onRemoveRow={draft.removeRow}
          onAddRow={draft.addRow}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * The panel's draft rows and the applied set they project to.
 *
 * `onApplyChange` fires only when that projection actually changes, so typing
 * into an incomplete row (or adding a blank one) doesn't churn the query or
 * reset pagination. It is seeded with the restored set, so a restore neither
 * re-emits filters the page already holds nor swallows the first edit made on
 * top of them (seeded rows are applied by construction, so they project back to
 * exactly that set).
 */
function useDraftFilterRows(
  initialFilters: CatalogAdvancedFilter[] | undefined,
  onApplyChange: (filters: CatalogAdvancedFilter[]) => void,
) {
  const [rows, setRows] = useState<DraftRow[]>(() =>
    toDraftRows(initialFilters ?? []),
  );
  // Seeded past the ids `toDraftRows` handed out, so a row added after a restore
  // can't collide with a restored one.
  const idCounter = useRef(rows.length);

  const lastAppliedRef = useRef<CatalogAdvancedFilter[]>(
    initialFilters ?? NO_ADVANCED_FILTERS,
  );
  const mutateRows = useCallback(
    (next: DraftRow[]) => {
      setRows(next);
      const applied = appliedFilters(next);
      if (sameAdvancedFilters(applied, lastAppliedRef.current)) return;
      lastAppliedRef.current = applied;
      onApplyChange(applied);
    },
    [onApplyChange],
  );

  return {
    rows,
    appliedCount: appliedFilters(rows).length,
    // A blank row applies nothing, so adding one needs no re-notification. The
    // id is minted here rather than inside the updater: React may run an updater
    // more than once, and advancing the counter is not something to repeat.
    addRow: () => {
      const id = `f${(idCounter.current += 1)}`;
      setRows((prev) => [
        ...prev,
        { id, column: '', operator: 'contains' as const, value: '' },
      ]);
    },
    removeRow: (id: string) => mutateRows(rows.filter((r) => r.id !== id)),
    updateRow: (id: string, patch: Partial<DraftRow>) =>
      mutateRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r))),
  };
}

/** The panel's own trigger, for the uncontrolled (in-place) arrangement. */
function InlineFiltersTrigger({
  open,
  activeCount,
}: {
  open: boolean;
  activeCount: number;
}) {
  const gt = useGT();
  return (
    <div className="flex items-center gap-2">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 px-2 text-sm">
          <SlidersHorizontalIcon className="h-4 w-4" />
          <T context="toggle label for advanced filters section">
            Advanced filters
          </T>
          <CaretDownIcon
            className={cn(
              'h-3.5 w-3.5 opacity-60 transition-transform',
              !open && '-rotate-90',
            )}
          />
        </Button>
      </CollapsibleTrigger>
      {activeCount > 0 && (
        <Badge variant="secondary" className="text-xs">
          {gt('{count} active', {
            context: 'badge showing number of active advanced filters',
            count: String(activeCount),
          })}
        </Badge>
      )}
    </div>
  );
}

/**
 * The match-mode switch: whether the constraints have to be met by one source
 * record or by the item across all of the records linked to it.
 *
 * It sits under the rows rather than beside the trigger because it only means
 * anything once there are constraints to combine, and reading it in the same
 * glance as them is what makes "and by the same source record" above legible as
 * something that can be changed.
 */
function FilterMatchToggle({
  match,
  onChange,
}: {
  match: CatalogFilterMatch;
  onChange: (match: CatalogFilterMatch) => void;
}) {
  const switchId = useId();
  return (
    <div className="space-y-1 border-t pt-2">
      <div className="flex items-center gap-2">
        <Switch
          id={switchId}
          checked={match === 'product'}
          onCheckedChange={(checked) => onChange(checked ? 'product' : 'row')}
        />
        <Label htmlFor={switchId} className="text-xs font-normal">
          <T context="switch enabling advanced filters to be satisfied across several AS/400 source files instead of by one record">
            Match across source files
          </T>
        </Label>
      </div>
      <p className="pl-10 text-xs text-muted-foreground">
        {match === 'product' ? (
          <T context="help text when advanced filters match across source files">
            Constraints on different AS/400 files are checked against the whole
            item, so a filter can combine columns that never share a record —
            but they must be met at the same branch. Stock and GAINS records
            letter their locations differently and count wherever they sit. The
            AS/400 view still matches record by record, so it can come back
            empty while this list fills.
          </T>
        ) : (
          <T context="help text when advanced filters must all be met by one source record">
            One source record has to meet every constraint, so columns from
            different AS/400 files never match together.
          </T>
        )}
      </p>
    </div>
  );
}

/** The constraint rows, the rejected-filter hint, and the "Add filter" button. */
function FilterRowsPanel({
  rows,
  columns,
  includeBaseColumns,
  columnsLoading,
  errorMessage,
  match,
  onMatchChange,
  onChangeRow,
  onRemoveRow,
  onAddRow,
}: {
  rows: DraftRow[];
  columns: FilterColumnOption[];
  includeBaseColumns: boolean;
  columnsLoading: boolean;
  errorMessage?: string | null;
  // Omitted by the views that only ever match record-by-record (the Export
  // tab), which then don't offer the switch at all.
  match?: CatalogFilterMatch;
  onMatchChange?: (match: CatalogFilterMatch) => void;
  onChangeRow: (id: string, patch: Partial<DraftRow>) => void;
  onRemoveRow: (id: string) => void;
  onAddRow: () => void;
}) {
  return (
    <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground">
          <T context="empty state for advanced filters before any row is added">
            Add a constraint to filter by a product or metadata column.
          </T>
        </p>
      )}

      {rows.length > 1 && (
        <p className="text-xs text-muted-foreground">
          {match === 'product' ? (
            <T context="explanation above the advanced filter rows when they are matched across source files">
              Every constraint must be met by the item across its source records
              at the same branch.
            </T>
          ) : (
            <T context="explanation above the advanced filter rows of how several constraints combine">
              Every constraint must be met, and by the same source record.
            </T>
          )}
        </p>
      )}

      {rows.map((row, index) => (
        <div key={row.id} className="space-y-2">
          {index > 0 && <FilterConjunction />}
          <FilterRow
            row={row}
            columns={columns}
            includeBaseColumns={includeBaseColumns}
            columnsLoading={columnsLoading}
            onChange={(patch) => onChangeRow(row.id, patch)}
            onRemove={() => onRemoveRow(row.id)}
          />
        </div>
      ))}

      {errorMessage && (
        <div className="flex items-center gap-1.5 text-xs text-destructive">
          <WarningIcon className="h-3.5 w-3.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 text-xs"
        onClick={onAddRow}
      >
        <PlusIcon className="h-3.5 w-3.5" />
        <T context="button to add another advanced filter constraint">
          Add filter
        </T>
      </Button>

      {match !== undefined && onMatchChange !== undefined && (
        <FilterMatchToggle match={match} onChange={onMatchChange} />
      )}
    </div>
  );
}

// Standalone toggle for a controlled `AdvancedFilters hideTrigger`, so the
// button can sit apart from the panel (e.g. beside the search bar) while the
// panel renders full-width below. `activeCount` is the number of applied
// filters (owned by the parent).
export function AdvancedFiltersToggle({
  open,
  onToggle,
  activeCount,
}: {
  open: boolean;
  onToggle: () => void;
  activeCount: number;
}) {
  const gt = useGT();
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 px-2 text-sm"
        aria-expanded={open}
        onClick={onToggle}
      >
        <SlidersHorizontalIcon className="h-4 w-4" />
        <T context="toggle label for advanced filters section">
          Advanced filters
        </T>
        <CaretDownIcon
          className={cn(
            'h-3.5 w-3.5 opacity-60 transition-transform',
            !open && '-rotate-90',
          )}
        />
      </Button>
      {activeCount > 0 && (
        <Badge variant="secondary" className="text-xs">
          {gt('{count} active', {
            context: 'badge showing number of active advanced filters',
            count: String(activeCount),
          })}
        </Badge>
      )}
    </div>
  );
}
